import React, { useState } from 'react'
import { CreateDatasetModal } from '../../../Source/NewDatasetModal/CreateDatasetModal/CreateDatasetModal'
import { EVENTS, trackEvent } from '../../../../../events'
import FileUpload from '../../../../Objects/FileUpload/FileUpload'
import { ConnectDatasource } from '../../../Source/NewDatasetModal/ConnectDatasource/ConnectDatasource'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { switchWorkspace } from '../../../../../store/actions/authActions'
import { setResetTrigger } from '../../../../../store/actions/uiActions'
import { useWorkspaces } from '../../../../../api/hooks/workspaces/useWorkspaces'
import { useSystemMessages } from '../../../../../context/SystemMessagesContext'
import { CircleNotchIcon } from '@phosphor-icons/react'
import { useProcessingPollingContext } from '../../../../../context/ProcessingPollingContext'
import { ScoopButton, ScoopText, ScoopColumn, ScoopSpinner } from '../../../../ui'
import styles from './DatasetStep.module.scss'

export const DatasetStep = ({
    title = 'Connect Your Data',
    description = 'Choose your source to see insights instantly.',
    setCreatedInbox,
    processingPolling,
    processingPollingIsTakingMoreThanExpected,
    reachedMaxPolling,
    inbox,
    personalWorkspace = null, // Pass personal workspace to switch before upload (for tour-to-personal flow)
    tourWorkspace = null, // Pass tour workspace to switch back if user cancels
}) => {
    const [selectedSource, setSelectedSource] = useState('')
    const [fileUploadOpen, setFileUploadOpen] = useState(false)
    const [applicationOpen, setApplicationOpen] = useState(false)
    const [switchedToPersonal, setSwitchedToPersonal] = useState(false) // Track if we switched for upload
    const { refetch: getMetadata, workspaceMetadata } = useWorkspaceMetadata({ tableStats: true })
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const defaultTourWorkspace = useSelector((state) => state.auth.defaultTourWorkspace)
    const currentWorkspaceID = useSelector((state) => state.auth.workspaceID)
    const { workspaces } = useWorkspaces()
    const { showInfo, showError } = useSystemMessages()
    const { startPolling } = useProcessingPollingContext()

    const handleSelectSource = (option, optionalSource) => {
        // If coming from Tour, switch to personal workspace before opening upload
        if (personalWorkspace) {
            dispatch(switchWorkspace(personalWorkspace.id, personalWorkspace.name))
            setSwitchedToPersonal(true)
        }
        if (optionalSource) setSelectedSource(optionalSource)
        if (option === 'File Upload') setFileUploadOpen(true)
        else setApplicationOpen(true)
    }

    // Switch back to Tour if user cancels without uploading
    const handleCancelUpload = () => {
        if (switchedToPersonal && tourWorkspace) {
            dispatch(switchWorkspace(tourWorkspace.id, tourWorkspace.name))
            setSwitchedToPersonal(false)
        }
    }

    const moveToTourWorkspace = () => {
        // Find the tour workspace
        showInfo('We are processing your data in the background.', {
            dismissible: false,
            leftSide: (
                <CircleNotchIcon size={20} weight="bold" color="#2b65d9" className="spinning" />
            ),
        })
        const tourWorkspace =
            workspaces.find((w) => w.workspaceID === defaultTourWorkspace) ||
            workspaces.find((w) => w.name === 'Tour Scoop')

        if (!inbox || !inbox.inboxID || !tourWorkspace) {
            showError('We could not process your dataset. Please try again or contact support.', {
                dismissible: true,
            })
            navigate('/sources')
            return
        }

        // Save the polling data before switching workspace
        const pollingData = {
            inbox: inbox,
            workspaceID: currentWorkspaceID, // The workspace where the inbox was created
            workspaceName: workspaceMetadata?.workspaceName,
        }

        startPolling(pollingData)

        // Now switch to Tour workspace
        dispatch(switchWorkspace(tourWorkspace.workspaceID, tourWorkspace.name))
        dispatch(setResetTrigger())

        navigate('/sources')
    }

    if (reachedMaxPolling) {
        return (
            <ShowProccessSatus
                title="We couldn't process your data in time."
                subtitle="Explore our tour workspace, meanwhile we continue processing your workspace."
            >
                <ScoopButton onClick={moveToTourWorkspace} size="xs">
                    Go to Tour Workspace
                </ScoopButton>
            </ShowProccessSatus>
        )
    }

    if (processingPollingIsTakingMoreThanExpected) {
        return (
            <ShowProccessSatus
                title="Your data is still being processed and understood by Scoop. Why not take a coffee break? ☕"
                subtitle="Please keep this tab open until setup is complete"
                hasSpinner
            />
        )
    }

    if (processingPolling) {
        return (
            <ShowProccessSatus
                title="We are currently processing your data - this may take up to a few minutes"
                subtitle="Please keep this tab open until setup is complete"
                hasSpinner
            />
        )
    }

    return (
        <>
            <ScoopColumn className={styles.container}>
                <ScoopColumn className={styles.header}>
                    <ScoopText className={styles.title}>{title}</ScoopText>
                    <ScoopText className={styles.description}>{description}</ScoopText>
                </ScoopColumn>
                <CreateDatasetModal isOnboarding handleSelectSource={handleSelectSource} />
            </ScoopColumn>
            <FileUpload
                isOnboarding
                isOpen={fileUploadOpen}
                onClose={() => {
                    setFileUploadOpen(false)
                    handleCancelUpload()
                }}
                goNext={(inboxName) => {
                    getMetadata().then((res) => {
                        const inbox = res.data.inboxes.find((inbox) => inbox.label === inboxName)
                        if (inbox) setCreatedInbox(inbox)
                        trackEvent(EVENTS.ONBOARDING.DATA_UPLOAD, {
                            file: { name: inboxName },
                            inbox: { inboxID: inbox.inboxID },
                        })
                        if (window.dataLayer) {
                            window.dataLayer.push({ event: 'user_data_added' })
                        }
                        setFileUploadOpen(false)
                    })
                }}
                stepBack={() => {
                    setFileUploadOpen(false)
                    handleCancelUpload()
                }}
            />
            {applicationOpen && (
                <ConnectDatasource
                    onClose={() => {
                        setApplicationOpen(false)
                        handleCancelUpload()
                    }}
                    stepBack={() => {
                        setApplicationOpen(false)
                        handleCancelUpload()
                    }}
                    source={selectedSource.sourceValue}
                    sourceStep={selectedSource.step}
                    goNext={(inboxName) => {
                        getMetadata().then((res) => {
                            const inbox = res.data.inboxes.find(
                                (inbox) => inbox.label === inboxName
                            )
                            if (inbox) setCreatedInbox(inbox)
                            setApplicationOpen(false)
                        })
                    }}
                />
            )}
        </>
    )
}

const ShowProccessSatus = ({ title, subtitle, hasSpinner, children }) => {
    return (
        <ScoopColumn className={styles.statusContainer}>
            {hasSpinner && <ScoopSpinner size={80} />}
            <ScoopText className={styles.statusTitle}>{title}</ScoopText>
            <ScoopText className={styles.statusSubtitle}>{subtitle}</ScoopText>
            {children}
        </ScoopColumn>
    )
}
